/**
 * /use-cases/engineering — "from bug report to a tested change proposal".
 * Namespace `ucEngineering`. Dutch is the lead language for this page (the
 * demo video is Dutch); EN stays in lockstep as a faithful translation.
 *
 * Claims discipline: FixControl prepares pull requests after the required
 * approval — it never merges and never pushes to the default branch (the
 * delivery path is PR/MR-only, enforced in the product; see CLAUDE.md
 * "External writes"). The "never straight to the main branch" line below is
 * backed by that shipped behavior.
 */

export const ucEngineeringEn = {
  meta: {
    title: "Bug and code automation with approval",
    description:
      "See how FixControl investigates bugs, prepares code changes, checks tests and prepares a pull request after approval.",
  },
  hero: {
    eyebrow: "Engineering",
    title: "From bug report to a",
    highlight: "tested change proposal",
    subtitle:
      "FixControl links a bug to the right project, investigates the relevant repository and documentation, and has specialized AI agents prepare a possible change.",
    primaryCta: "Book a technical demo",
    secondaryCta: "Watch the video",
    bullet1: "Changes are tested before anyone reviews them",
    bullet2: "An engineer on your team decides what moves forward",
    bullet3: "Delivered as a pull request — never straight to the main branch",
  },
  video: {
    eyebrow: "The demo",
    title: "Watch the engineering flow",
    description: "From a bug report to a tested change and a prepared pull request.",
  },
  how: {
    eyebrow: "Step by step",
    title: "What happens with a bug report",
    description: "The same flow as in the video, step by step and in plain language.",
    steps: {
      s1: { tag: "Report", title: "A bug is reported", body: "Through support, chat or your issue tracker — the report stays in the system it came from." },
      s2: { tag: "Project", title: "FixControl finds the project and the repository", body: "The report is linked to the project that holds the right code and documentation." },
      s3: { tag: "Research", title: "Agents investigate the problem", body: "They read the code involved, earlier reports and recent changes to find a likely cause." },
      s4: { tag: "Change", title: "A change is prepared", body: "A concrete code change that addresses the cause. Nothing has been committed anywhere yet." },
      s5: { tag: "Tests", title: "Tests are run", body: "The change runs in an isolated test environment; the test results are recorded with the proposal." },
      s6: { tag: "Risk", title: "Risk and approval are determined", body: "FixControl determines the risk of the change and which approval your rules require." },
      s7: { tag: "Decision", title: "An engineer on your team decides", body: "That engineer — someone on your own team — sees the change, the test results and the risk — and approves, rejects or asks for adjustments." },
      s8: { tag: "Pull request", title: "A pull request is prepared", body: "After the required approval the change is delivered as a pull request for normal code review. Never straight to the main branch." },
    },
  },
  review: {
    eyebrow: "Reviewable work",
    title: "From analysis to reviewable work",
    body:
      "FixControl prepares the change and makes the checks that ran visible. An engineer on your team keeps the decision about what moves forward.",
  },
  related: {
    support: {
      label: "Also see support and email automation",
      description: "From a question by a customer of your organization to a checked reply, with the right tickets, conversations and documentation.",
    },
    remediation: {
      label: "Support-to-engineering remediation",
      description: "The full path from a support ticket to a governed code fix.",
    },
    github: {
      label: "GitHub integration",
      description: "How prepared changes arrive as pull requests in your repositories.",
    },
  },
  faq: {
    eyebrow: "Questions",
    title: "Frequently asked about code automation",
    q1: {
      q: "Does FixControl change code directly?",
      a: "No. FixControl prepares a change, tests it in an isolated environment and delivers it as a pull request after the required approval. Never straight to the main branch.",
    },
    q2: {
      q: "How do we know a change is safe?",
      a: "The proposal carries the tests that ran and the risk that was determined. An engineer on your team sees those checks and decides what moves forward.",
    },
    q3: {
      q: "Which code does FixControl use?",
      a: "Only the repositories you link to the project. Investigation happens with read access; changes go through a pull request after approval.",
    },
    q4: {
      q: "Which systems does this work with?",
      a: "GitHub and GitLab for code; reports can come in through support, Slack, Teams or your issue tracker.",
    },
  },
  cta: {
    title: "See the engineering flow on a real bug",
    description: "In one technical demo we walk from bug report to a tested change proposal and pull request.",
    primary: "Book a technical demo",
    secondary: "Contact us",
  },
};

export const ucEngineeringNl = {
  meta: {
    title: "Bug- en codeautomatisering met goedkeuring",
    description:
      "Bekijk hoe FixControl bugs onderzoekt, codewijzigingen voorbereidt, tests controleert en na goedkeuring een pull request aanmaakt.",
  },
  hero: {
    eyebrow: "Engineering",
    title: "Van bugmelding naar",
    highlight: "getest wijzigingsvoorstel",
    subtitle:
      "FixControl koppelt een bug aan het juiste project, onderzoekt de relevante repository en documentatie en laat gespecialiseerde AI-agents een mogelijke wijziging voorbereiden.",
    primaryCta: "Plan een technische demo",
    secondaryCta: "Bekijk de video",
    bullet1: "Wijzigingen worden getest vóór iemand ze beoordeelt",
    bullet2: "Een engineer van je team beslist wat er verdergaat",
    bullet3: "Opgeleverd als pull request — niet rechtstreeks naar de hoofdbranch",
  },
  video: {
    eyebrow: "De demo",
    title: "Bekijk de engineeringflow",
    description: "Van een bugmelding naar een geteste wijziging en een voorbereide pull request.",
  },
  how: {
    eyebrow: "Stap voor stap",
    title: "Wat er met een bugmelding gebeurt",
    description: "Dezelfde flow als in de video, stap voor stap en in gewone taal.",
    steps: {
      s1: { tag: "Melding", title: "Een bug wordt gemeld", body: "Via support, chat of je issuetracker — de melding blijft staan in het systeem waar die binnenkwam." },
      s2: { tag: "Project", title: "FixControl vindt het project en de repository", body: "De melding wordt gekoppeld aan het project met de juiste code en documentatie." },
      s3: { tag: "Onderzoek", title: "Agents onderzoeken het probleem", body: "Ze lezen de betrokken code, eerdere meldingen en recente wijzigingen om een waarschijnlijke oorzaak te vinden." },
      s4: { tag: "Wijziging", title: "Een wijziging wordt voorbereid", body: "Een concrete codewijziging die de oorzaak aanpakt. Er is nog nergens iets vastgelegd." },
      s5: { tag: "Tests", title: "Tests worden uitgevoerd", body: "De wijziging draait in een afgeschermde testomgeving; de testresultaten worden bij het voorstel vastgelegd." },
      s6: { tag: "Risico", title: "Risico en goedkeuring worden bepaald", body: "FixControl bepaalt het risico van de wijziging en welke goedkeuring je regels vereisen." },
      s7: { tag: "Besluit", title: "Een engineer van je team beslist", body: "Die engineer — iemand van je eigen team — ziet de wijziging, de testresultaten en het risico — en keurt goed, wijst af of vraagt aanpassingen." },
      s8: { tag: "Pull request", title: "Een pull request wordt voorbereid", body: "Na de vereiste goedkeuring wordt de wijziging als pull request opgeleverd voor normale codereview. Niet rechtstreeks naar de hoofdbranch." },
    },
  },
  review: {
    eyebrow: "Reviewbaar werk",
    title: "Van analyse naar reviewbaar werk",
    body:
      "FixControl bereidt de wijziging voor en maakt de uitgevoerde controles zichtbaar. Een engineer van je team houdt de beslissing over wat er verdergaat.",
  },
  related: {
    support: {
      label: "Bekijk ook support- en e-mailautomatisering",
      description: "Van een vraag van een klant van je organisatie naar een gecontroleerd antwoord, met de juiste tickets, gesprekken en documentatie.",
    },
    remediation: {
      label: "Van supportticket naar code-oplossing",
      description: "Het volledige pad van een supportticket naar een gecontroleerde codefix.",
    },
    github: {
      label: "GitHub-integratie",
      description: "Hoe voorbereide wijzigingen als pull requests in je repositories terechtkomen.",
    },
  },
  faq: {
    eyebrow: "Vragen",
    title: "Veelgestelde vragen over bugs en code",
    q1: {
      q: "Past FixControl code direct aan?",
      a: "Nee. FixControl bereidt een wijziging voor, test die in een afgeschermde omgeving en levert die na de vereiste goedkeuring op als pull request. Niet rechtstreeks naar de hoofdbranch.",
    },
    q2: {
      q: "Hoe weten we of een wijziging veilig is?",
      a: "Bij het voorstel staan de uitgevoerde tests en het vastgestelde risico. Een engineer van je team ziet die controles en beslist wat er verdergaat.",
    },
    q3: {
      q: "Welke code gebruikt FixControl?",
      a: "Alleen de repositories die je aan het project koppelt. Onderzoek gebeurt met leesrechten; wijzigingen verlopen via een pull request na goedkeuring.",
    },
    q4: {
      q: "Met welke systemen werkt dit?",
      a: "GitHub en GitLab voor code; meldingen kunnen binnenkomen via support, Slack, Teams of je issuetracker.",
    },
  },
  cta: {
    title: "Bekijk de engineeringflow op een echte bug",
    description: "In één technische demo lopen we van bugmelding naar getest wijzigingsvoorstel en pull request.",
    primary: "Plan een technische demo",
    secondary: "Neem contact op",
  },
};
